import { BidSegment } from '@ebalance/proto/bfftypes/v1/bid_segment_pb'

import { useBidLimits } from '~/hooks/bidding/useBidLimits'
import type { BidLimits } from '~/models/bidLimits'

/**
 * ブロック入札に適用する (エリア × 売買) 入札制限を解決する page-local hook。
 *
 * spec `bidding/bid-limits.md` (:7 / §1-2 :120 / Discussion :326、 2026-07-23 入札相談会
 * 確定) は「上下限価格・上限数量は **通常入札・ブロック入札で共通の 1 マトリクス**」
 * 「ブロック専用の必須化トグルは撤廃」 と規定しており、 必須化トグルの ON/OFF による
 * 例外は無い。 design 正本 (`design/bidding/spot.html` の `_getBidLimitForSpot` が
 * 共通の `p.spot` を引き、 `_isBlockLimitRequired` は常に false) と mock-app
 * (`JepxSpotBid.Block.cs` の block form が `_bidLimits.Spot` を引く) も同じ。
 *
 * 実装には **ブロック専用 matrix (`BidSegment.SPOT_BLOCK`) + 必須化トグル**が
 * 残存しているが (spec 削除済み機能の残存 = drift、 削除は issue #6565)、
 * 本 hook はトグルの状態に関わらず常に `SPOT_NORMAL` matrix のみを参照する
 * (backend `BlockBidUsecase.ensureWithinBidVolumeLimit` の作成・保存ゲートと
 * 同じ規則に揃えている。 以前はトグル ON の間だけ `SPOT_BLOCK` を参照していたが、
 * spec の共通マトリクス規定を見落としていたため撤回した)。
 *
 * **pass-through のまま残すのは意図的**: 現状は `useBidLimits(SPOT_NORMAL)`
 * を返すだけで caller が直に呼んでも動くが、 「ブロック入札がどの matrix を
 * 見るか」 という判断を名前のある 1 箇所に残すため #6565 まで維持する。 #6565 で
 * BidSegment が spot に統合されたら、 本 hook は
 * `useBidLimits(<統合後の spot segment>)` の re-export に縮退するか、
 * caller 直呼びに畳んで削除する。
 *
 * **既知の drift (#6565)**: 必須化トグルが ON の組織では設定画面が今も
 * SPOT_BLOCK の matrix を描画・保存するが (`AdminBidLimits/tabs/SpotBlockTab`)、
 * 本 hook も backend ゲートもその値を参照しない。 SPOT_BLOCK にだけ値を入れ
 * SPOT_NORMAL が空欄の組織では、 画面で編集できる 上限数量(1札) がどの判定にも
 * 効かない状態になる (= 表示と判定の食い違い)。 spec 側は共通 1 マトリクスで
 * 確定済みなので実装の正は本 hook 側にあり、 残る手当て (タブ / トグルの削除・
 * 暫定の画面注記・既存設定値の移行要否) は #6565 で扱う。
 */
export function useBlockBidLimits(): {
  limits: BidLimits
  isLoading: boolean
  /** 取り直し中を含む取得中。 取り直す前の値で判断してはいけない呼び出し元が見る。 */
  isFetching: boolean
  isError: boolean
  /** 入札制限を取り直す。 */
  refetch: () => Promise<unknown>
} {
  const { limits, isLoading, isFetching, isError, refetch } = useBidLimits(
    BidSegment.SPOT_NORMAL,
  )
  return { limits, isLoading, isFetching, isError, refetch }
}
